import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Plus,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Trash2,
  KeyRound,
  Copy,
  Eye,
  EyeOff,
  Check,
  Search,
} from "lucide-react";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  adminDrawerBodyStyle,
  adminFieldStyle,
  adminInputStyle,
  adminLabelStyle,
  adminMutedTextStyle,
  adminSelectStyle,
} from "@/components/admin/adminFormStyles";
import {
  useUserPage,
  useCreateUser,
  useUpdateUser,
  useDeactivateUser,
  useReactivateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUserStoreAccess,
  useUserAccessStores,
} from "@/features/user/hooks";
import { useClients } from "@/features/client/hooks";
import { useCreateSupplier, useSuppliers } from "@/features/supplier/hooks";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import {
  USER_ROLE_LABELS,
  UserDirectory,
  UserDirectorySkeleton,
} from "@/components/users/UserDirectory";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import type {
  StoreAccessScope,
  UserListItem,
  UserRole,
} from "@/features/user/types";
import {
  EDITABLE_USER_ROLES,
  canActorEditStoreAccess,
  canActorEditUserIdentity,
  generateInitialPassword,
} from "./userManagementRules";

const ROLE_LABELS = USER_ROLE_LABELS;
const USER_PAGE_SIZE = 10;

type DrawerMode = "create" | "edit";

interface DrawerState {
  mode: DrawerMode;
  user?: UserListItem;
}

type UserQueryState = {
  offset: number;
  search: string;
  role: string;
  active: boolean | null;
};

function getValidUserOffset(total: number, offset: number) {
  if (total <= 0) return 0;
  return Math.min(
    Math.max(0, offset),
    Math.floor((total - 1) / USER_PAGE_SIZE) * USER_PAGE_SIZE,
  );
}

export function UsuariosPage() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const isSuper = currentUser?.role === "super";
  const isClientManager = currentUser?.role === "client";
  const canCreateUsers = isSuper;

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState<UserQueryState>({
    offset: 0,
    search: "",
    role: "",
    active: null,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<
    Pick<UserQueryState, "role" | "active">
  >({ role: "", active: null });
  const [dismissedListError, setDismissedListError] = useState<unknown>(null);

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("client");
  const [clientId, setClientId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [password, setPassword] = useState("");
  const [resetPassword, setResetPasswordValue] = useState("");
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [supplierCreateOpen, setSupplierCreateOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierEmail, setNewSupplierEmail] = useState("");
  const [newSupplierPhone, setNewSupplierPhone] = useState("");
  const [storeScope, setStoreScope] = useState<StoreAccessScope>("all");
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [storeSearch, setStoreSearch] = useState("");

  const debouncedSearch = useDebouncedValue(searchInput, 250).trim();
  const userPage = useUserPage(
    {
      limit: USER_PAGE_SIZE,
      offset: query.offset,
      search: query.search,
      role: query.role,
      active: query.active,
    },
    isSuper || isClientManager,
  );

  const activeUser = drawer?.user;
  const isSelf = activeUser?.id === currentUser?.id;
  const canEditActiveIdentity =
    !!currentUser &&
    !!activeUser &&
    canActorEditUserIdentity(currentUser, activeUser);
  const canEditActiveStoreAccess =
    !!currentUser &&
    !!activeUser &&
    isClientManager &&
    canActorEditStoreAccess(currentUser, activeUser);
  const isStoreAccessOnlyDrawer =
    drawer?.mode === "edit" &&
    canEditActiveStoreAccess &&
    !canEditActiveIdentity;
  const identityDrawerOpen = isSuper && drawer !== null;

  const { data: clients } = useClients({
    enabled:
      identityDrawerOpen && (role === "client" || role === "client_store"),
  });
  const { data: suppliers } = useSuppliers({
    enabled: identityDrawerOpen && role === "supplier",
  });
  const { data: storesData } = useUserAccessStores(isStoreAccessOnlyDrawer);

  const createMutation = useCreateUser();
  const createSupplierMutation = useCreateSupplier();
  const updateMutation = useUpdateUser();
  const updateStoreAccessMutation = useUpdateUserStoreAccess();
  const deactivateMutation = useDeactivateUser();
  const reactivateMutation = useReactivateUser();
  const deleteMutation = useDeleteUser();
  const resetPasswordMutation = useResetUserPassword();

  useEffect(() => {
    setQuery((current) =>
      current.search === debouncedSearch
        ? current
        : { ...current, offset: 0, search: debouncedSearch },
    );
  }, [debouncedSearch]);

  useEffect(() => {
    if (!userPage.data || userPage.isPlaceholderData) return;
    const validOffset = getValidUserOffset(userPage.data.total, query.offset);
    if (validOffset === query.offset) return;
    setQuery((current) => ({ ...current, offset: validOffset }));
  }, [query.offset, userPage.data, userPage.isPlaceholderData]);

  useEffect(() => {
    if (!userPage.isError) setDismissedListError(null);
  }, [userPage.isError]);

  function openCreate() {
    if (!canCreateUsers) return;
    setName("");
    setEmail("");
    setRole("client");
    setClientId("");
    setSupplierId("");
    setPassword("");
    setCopiedPassword(false);
    setConfirmDelete(false);
    setConfirmReset(false);
    setShowPassword(false);
    setShowResetPassword(false);
    setDrawer({ mode: "create" });
  }

  function openEdit(user: UserListItem) {
    const canOpen =
      !!currentUser &&
      (canActorEditUserIdentity(currentUser, user) ||
        (isClientManager && canActorEditStoreAccess(currentUser, user)));
    if (!canOpen) return;
    setName(user.name);
    setEmail(user.email);
    setRole(user.role);
    setClientId(user.client_id ?? "");
    setSupplierId(user.supplier_id ?? "");
    setPassword("");
    setResetPasswordValue("");
    setCopiedPassword(false);
    setConfirmDelete(false);
    setConfirmReset(false);
    setShowPassword(false);
    setShowResetPassword(false);
    setStoreScope(user.store_access?.scope_type ?? "all");
    setSelectedStoreIds(user.store_access?.store_ids ?? []);
    setStoreSearch("");
    setDrawer({ mode: "edit", user });
  }

  function closeDrawer() {
    createSupplierMutation.reset();
    setDrawer(null);
    setConfirmDelete(false);
    setConfirmReset(false);
    setCopiedPassword(false);
    setSupplierCreateOpen(false);
    setShowPassword(false);
    setShowResetPassword(false);
    setStoreSearch("");
  }

  function openSupplierCreate() {
    createSupplierMutation.reset();
    setNewSupplierName("");
    setNewSupplierEmail("");
    setNewSupplierPhone("");
    setSupplierCreateOpen(true);
  }

  function closeSupplierCreate() {
    createSupplierMutation.reset();
    setSupplierCreateOpen(false);
    setNewSupplierName("");
    setNewSupplierEmail("");
    setNewSupplierPhone("");
  }

  async function handleCreate() {
    if (!canCreateUsers) return;
    if (!name.trim() || !email.trim() || !password.trim()) return;
    const payload: Parameters<typeof createMutation.mutateAsync>[0] = {
      name: name.trim(),
      email: email.trim(),
      role,
      password: password.trim(),
    };
    if ((role === "client" || role === "client_store") && clientId) {
      payload.client_id = clientId;
    }
    if (role === "supplier" && supplierId) payload.supplier_id = supplierId;

    try {
      await createMutation.mutateAsync(payload);
      closeDrawer();
    } catch {
      // error stored in createMutation.error, displayed in drawer
    }
  }

  async function handleUpdate() {
    if (!canEditActiveIdentity) return;
    if (!activeUser || !name.trim() || !email.trim()) return;
    const payload: Parameters<typeof updateMutation.mutateAsync>[0]["payload"] =
      {
        name: name.trim(),
        email: email.trim(),
        role,
        client_id:
          (role === "client" || role === "client_store") && clientId
            ? clientId
            : null,
        supplier_id: role === "supplier" && supplierId ? supplierId : null,
      };
    try {
      await updateMutation.mutateAsync({ id: activeUser.id, payload });
      closeDrawer();
    } catch {
      // error stored in updateMutation.error, displayed in drawer
    }
  }

  async function handleDeactivate() {
    if (!canEditActiveIdentity) return;
    if (!activeUser) return;
    await deactivateMutation.mutateAsync(activeUser.id);
    closeDrawer();
  }

  async function handleReactivate() {
    if (!canEditActiveIdentity) return;
    if (!activeUser) return;
    await reactivateMutation.mutateAsync(activeUser.id);
    closeDrawer();
  }

  async function handleDelete() {
    if (!canEditActiveIdentity) return;
    if (!activeUser) return;
    await deleteMutation.mutateAsync(activeUser.id);
    closeDrawer();
  }

  async function handleResetPassword() {
    if (!canEditActiveIdentity) return;
    if (!activeUser || !resetPassword.trim()) return;
    await resetPasswordMutation.mutateAsync({
      id: activeUser.id,
      payload: { new_password: resetPassword.trim() },
    });
    setConfirmReset(false);
    setResetPasswordValue("");
    closeDrawer();
  }

  async function handleCreateSupplierInline() {
    if (!canCreateUsers) return;
    if (!newSupplierName.trim()) return;
    try {
      const created = await createSupplierMutation.mutateAsync({
        name: newSupplierName.trim(),
        email: newSupplierEmail.trim() || undefined,
        phone: newSupplierPhone.trim() || undefined,
      });
      setRole("supplier");
      setSupplierId(created.id);
      closeSupplierCreate();
    } catch {
      // error stored in createSupplierMutation.error, displayed in this drawer
    }
  }

  const isPending =
    createSupplierMutation.isPending ||
    createMutation.isPending ||
    updateMutation.isPending ||
    updateStoreAccessMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending ||
    deleteMutation.isPending ||
    resetPasswordMutation.isPending;

  const mutationError =
    createMutation.error ||
    updateMutation.error ||
    updateStoreAccessMutation.error ||
    deactivateMutation.error ||
    reactivateMutation.error ||
    deleteMutation.error ||
    resetPasswordMutation.error;

  async function handleCopyPassword() {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopiedPassword(true);
      window.setTimeout(() => setCopiedPassword(false), 1400);
    } catch {
      setCopiedPassword(false);
    }
  }

  const users = userPage.data?.items ?? [];
  const total = userPage.data?.total ?? 0;
  const hasRows = users.length > 0;
  const isFiltered = Boolean(
    query.search || query.role || query.active !== null,
  );
  const showRefetchError =
    hasRows && userPage.isError && dismissedListError !== userPage.error;
  const roleFilterOptions = [
    { value: "", label: "Todos os perfis" },
    ...(isSuper
      ? (Object.entries(ROLE_LABELS) as Array<[UserRole, string]>).map(
          ([value, label]) => ({ value, label }),
        )
      : [{ value: "client_store", label: "Responsáveis por loja" }]),
  ];
  const statusFilterOptions = [
    { value: "all", label: "Todos os estados" },
    { value: "active", label: "Ativos" },
    { value: "inactive", label: "Desativados" },
  ];
  const clientOptions = useMemo(() => {
    const options = [...(clients?.items ?? [])];
    if (clientId && !options.some((client) => client.id === clientId)) {
      options.unshift({
        id: clientId,
        name: activeUser?.client_name ?? "Cliente atual",
      } as (typeof options)[number]);
    }
    return options;
  }, [activeUser?.client_name, clientId, clients?.items]);
  const supplierOptions = useMemo(() => {
    const options = [...(suppliers?.items ?? [])];
    if (supplierId && !options.some((supplier) => supplier.id === supplierId)) {
      options.unshift({
        id: supplierId,
        name: activeUser?.supplier_name ?? "Fornecedor atual",
      } as (typeof options)[number]);
    }
    return options;
  }, [activeUser?.supplier_name, supplierId, suppliers?.items]);
  const isDeactivatedUser = !!activeUser?.deactivated_at;
  const stores = storesData?.items ?? [];
  const storeSearchQuery = storeSearch.trim().toLowerCase();
  const filteredAccessStores = useMemo(
    () =>
      stores.filter((store) => {
        if (!storeSearchQuery) return true;
        return (
          store.name.toLowerCase().includes(storeSearchQuery) ||
          store.address.toLowerCase().includes(storeSearchQuery)
        );
      }),
    [storeSearchQuery, stores],
  );
  const selectedStoreCount = selectedStoreIds.filter((storeId) =>
    stores.some((store) => store.id === storeId),
  ).length;

  function toggleStoreSelection(storeId: string, checked: boolean) {
    setSelectedStoreIds((current) =>
      checked
        ? Array.from(new Set([...current, storeId]))
        : current.filter((id) => id !== storeId),
    );
  }

  async function handleStoreAccessSubmit() {
    if (!activeUser || !canEditActiveStoreAccess) return;
    await updateStoreAccessMutation.mutateAsync({
      id: activeUser.id,
      payload: {
        scope_type: storeScope,
        store_ids: storeScope === "selected" ? selectedStoreIds : [],
      },
    });
    closeDrawer();
  }

  const btnPrimary: React.CSSProperties = {
    flex: 1,
    padding: "0.75rem",
    background: "var(--users-primary)",
    border: "none",
    borderRadius: "0.5rem",
    color: "var(--users-on-primary)",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: 0,
    cursor: isPending ? "not-allowed" : "pointer",
    opacity: isPending ? 0.6 : 1,
  };

  const btnSecondary: React.CSSProperties = {
    flex: 1,
    padding: "0.625rem",
    background: "none",
    border: "1px solid var(--login-border)",
    borderRadius: "0.5rem",
    color: "var(--login-muted)",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: 0,
    cursor: isPending ? "not-allowed" : "pointer",
    opacity: isPending ? 0.6 : 1,
  };

  const btnDanger: React.CSSProperties = {
    flex: 1,
    padding: "0.625rem",
    background: "none",
    border: "1px solid var(--login-error, #c44e3a)",
    borderRadius: "0.5rem",
    color: "var(--login-error, #c44e3a)",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: 0,
    cursor: isPending ? "not-allowed" : "pointer",
    opacity: isPending ? 0.6 : 1,
  };

  const toggleButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    width: "44px",
    height: "44px",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--login-muted)",
    display: "flex",
    alignItems: "center",
    lineHeight: 1,
  };

  return (
    <>
      <style>{`
        .app-shell-main:has(.users-directory__toolbar) { background: #fff; }
        .operational-page-shell:has(.users-directory__toolbar),
        :root:has(.users-directory__toolbar) .admin-drawer-panel {
          --login-bg: #fff;
          --login-surface: #fff;
          --login-text: #183f3b;
          --login-muted: #647b7c;
          --login-border: #dee7e8;
          --login-accent: #1d704b;
          --users-primary: #a0eb70;
          --users-on-primary: #183f3b;
        }
        .operational-page-shell:has(.users-directory__toolbar) {
          --app-page-gutter: clamp(24px, 5vw, 72px);
          --operational-control-surface: var(--login-surface);
          --operational-control-border: var(--login-border);
          --operational-control-ink: var(--login-text);
          --operational-control-muted: var(--login-muted);
          max-width: calc(1120px + 2 * var(--app-page-gutter));
          padding-inline: var(--app-page-gutter);
          padding-top: 32px;
        }
        :root[data-theme="dark"] .app-shell-main:has(.users-directory__toolbar) {
          background: #0e0f10;
        }
        :root[data-theme="dark"] .operational-page-shell:has(.users-directory__toolbar),
        :root[data-theme="dark"]:has(.users-directory__toolbar) .admin-drawer-panel {
          --login-bg: #202122;
          --login-surface: #202122;
          --login-text: #f2f0eb;
          --login-muted: #a6a9af;
          --login-border: #303332;
          --login-accent: #8db47a;
          --users-primary: #8db47a;
          --users-on-primary: #0e0f10;
        }
        :root[data-theme="dark"] .operational-page-shell:has(.users-directory__toolbar) {
          background: #0e0f10;
        }
        .operational-page-shell:has(.users-directory__toolbar) h1 {
          font-size: clamp(1.6rem, 4vw, 1.9rem) !important;
          letter-spacing: 0.03em !important;
        }
        .usuarios-add {
          display: inline-flex;
          min-height: 44px;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 15px;
          border: 0;
          border-radius: 10px;
          background: var(--users-primary);
          color: var(--users-on-primary);
          font: 700 14px 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 160ms ease, transform 160ms ease;
        }
        .usuarios-add:hover:not(:disabled) { background: color-mix(in srgb, var(--users-primary) 88%, var(--login-text)); }
        .usuarios-add:active:not(:disabled) { transform: scale(0.98); }
        .usuarios-add:focus-visible,
        :root:has(.users-directory__toolbar) .admin-drawer-panel button:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel :is(input, select):focus-visible {
          outline: 2px solid var(--login-accent) !important;
          outline-offset: 2px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel :is(input, select) {
          min-height: 44px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .password-toggle {
          border-radius: 10px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .drawer-input:has(+ .password-toggle) {
          padding-right: 50px !important;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel :is(.btn-primary, .btn-secondary, .btn-danger):disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .admin-drawer-close {
          min-width: 44px;
          min-height: 44px;
        }
        .operational-page-shell:has(.users-directory__toolbar) .users-ledger {
          border-top: 1px solid var(--login-border);
          border-bottom: 1px solid var(--login-border);
          border-radius: 0;
          box-shadow: none;
        }
        .users-directory__toolbar .operational-search-field input::placeholder,
        :root:has(.users-directory__toolbar) .admin-drawer-panel input::placeholder {
          color: var(--login-muted);
          opacity: 1;
        }
        .users-directory__filters .operational-select__trigger,
        :root:has(.users-directory__toolbar) .admin-drawer-panel .drawer-input {
          border-radius: 10px !important;
        }
        .users-directory__toolbar .operational-search-field {
          border-radius: 10px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel label:not(.users-store-check),
        :root:has(.users-directory__toolbar) .admin-drawer-panel :is(.btn-primary,.btn-secondary,.btn-danger) {
          font-family: 'DM Sans', sans-serif !important;
          font-size: 12px !important;
          letter-spacing: 0 !important;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel :is(.btn-primary,.btn-secondary,.btn-danger) {
          min-height: 44px;
          border-radius: 10px !important;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-primary:hover:not(:disabled) {
          opacity: 1;
          background: color-mix(in srgb, var(--users-primary) 88%, var(--login-text)) !important;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .users-store-scope {
          background: transparent;
          color: var(--login-muted);
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .users-store-scope[aria-pressed="true"] {
          background: #eaf7ed;
          color: #183f3b;
          box-shadow: inset 0 -2px #1d704b;
        }
        :root[data-theme="dark"]:has(.users-directory__toolbar) .admin-drawer-panel .users-store-scope[aria-pressed="true"] {
          background: #282a29;
          color: #f2f0eb;
          box-shadow: inset 0 -2px #8db47a;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .users-store-check:focus-within {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-close:hover {
          background: color-mix(in srgb, #b83232 10%, var(--login-surface)) !important;
          color: #b83232 !important;
        }
        :root:has(.users-directory__toolbar) .operational-select__menu[data-layer="overlay"] {
          --login-accent: #1d704b;
          border-color: #dee7e8;
          border-radius: 12px;
          background: #fff;
          color: #183f3b;
        }
        :root[data-theme="dark"]:has(.users-directory__toolbar) .operational-select__menu[data-layer="overlay"] {
          --login-accent: #8db47a;
          border-color: #303332;
          background: #202122;
          color: #f2f0eb;
        }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-primary { transition: background 0.15s; }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-secondary { transition: color 0.15s, border-color 0.15s; }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-secondary:hover:not(:disabled) { border-color: var(--login-muted); color: var(--login-text); }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-danger { transition: background 0.15s; }
        :root:has(.users-directory__toolbar) .admin-drawer-panel .btn-danger:hover:not(:disabled) { background: rgba(196,78,58,0.08) !important; }
        .drawer-input::placeholder {
          color: var(--login-muted);
          opacity: 1;
        }
        .password-toggle { color: var(--login-muted); }
        .password-toggle:hover { color: var(--login-text); }
        .eye-icon {
          display: flex;
          align-items: center;
          transition: opacity 0.15s ease;
        }
        .eye-icon.eye-open {
          opacity: 1;
          transform: none;
        }
        .eye-icon.eye-closed {
          opacity: 0.7;
          transform: none;
        }
        .users-directory__toolbar {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: start;
          gap: 0.625rem;
          margin-bottom: 0.75rem;
        }
        .users-directory__filters .users-filter-field {
          display: grid;
          gap: 0.4rem;
          color: var(--filter-muted);
          font: 700 12px 'DM Sans', sans-serif;
        }
        .operational-page-shell:has(.users-directory__filters .operational-filter-layer) {
          position: relative;
          z-index: 122;
          animation: none;
        }
        .users-directory__filters .operational-filter-layer {
          z-index: 130;
        }
        .users-directory__empty {
          display: grid;
          min-height: 10rem;
          place-items: center;
          padding: 1.5rem;
          border: 1px solid var(--login-border);
          border-radius: var(--operational-corner-md);
          background: var(--login-surface);
          color: var(--login-muted);
          text-align: center;
        }
        .users-directory__empty p { margin: 0; }
        @media (max-width: 640px) {
          .users-directory__filters .operational-filter-trigger {
            width: 44px;
            justify-content: center;
            padding-inline: 0;
          }
          .users-directory__filters .operational-filter-trigger > span,
          .users-directory__filters .operational-filter-quick-clear > span {
            display: none;
          }
          .users-directory__filters .operational-filter-quick-clear {
            width: 44px;
            justify-content: center;
          }
          .operational-page-shell:has(.users-directory__toolbar) {
            --app-page-gutter: 14px;
            padding-top: 20px;
          }
          .operational-page-shell:has(.users-directory__toolbar) .users-ledger {
            border: 0;
          }
          .operational-page-shell:has(.users-directory__toolbar) .users-ledger__data-row {
            border-bottom: 1px solid var(--login-border);
          }
          .operational-page-shell:has(.users-directory__toolbar) .users-ledger__data-row:last-child {
            border-bottom: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .usuarios-add, :root:has(.users-directory__toolbar) .admin-drawer-panel button { transition: none !important; }
          .usuarios-add:active { transform: none; }
        }
      `}</style>

      <AdminPageShell
        breadcrumb={
          <AdminBreadcrumb
            items={[
              { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
              { label: "Usuários" },
            ]}
          />
        }
        icon={ShieldCheck}
        size="standard"
        title="USUÁRIOS"
        action={
          canCreateUsers ? (
            <button
              aria-label="Adicionar"
              type="button"
              onClick={openCreate}
              className="usuarios-add"
            >
              <Plus size={16} strokeWidth={2} />
              Novo usuário
            </button>
          ) : null
        }
      >
        <div className="users-directory__toolbar">
          <OperationalSearchField
            ariaLabel="Buscar usuários"
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Buscar por nome ou e-mail..."
          />
          <div className="users-directory__filters">
            <OperationalFilterSurface
              open={filtersOpen}
              activeCount={
                Number(Boolean(query.role)) + Number(query.active !== null)
              }
              onOpenChange={(open) => {
                if (open)
                  setDraftFilters({ role: query.role, active: query.active });
                setFiltersOpen(open);
              }}
              onApply={() =>
                setQuery((current) => ({
                  ...current,
                  ...draftFilters,
                  offset: 0,
                }))
              }
              onCancel={() =>
                setDraftFilters({ role: query.role, active: query.active })
              }
              onClear={() => {
                setDraftFilters({ role: "", active: null });
                if (!filtersOpen) {
                  setQuery((current) => ({
                    ...current,
                    role: "",
                    active: null,
                    offset: 0,
                  }));
                }
              }}
            >
              <div className="users-filter-field">
                <span>Perfil</span>
                <OperationalSelect
                  ariaLabel="Filtrar por perfil"
                  value={draftFilters.role}
                  options={roleFilterOptions}
                  menuLayer="overlay"
                  onChange={(role) =>
                    setDraftFilters((current) => ({ ...current, role }))
                  }
                />
              </div>
              <div className="users-filter-field">
                <span>Estado</span>
                <OperationalSelect
                  ariaLabel="Filtrar por estado"
                  value={
                    draftFilters.active === null
                      ? "all"
                      : draftFilters.active
                        ? "active"
                        : "inactive"
                  }
                  options={statusFilterOptions}
                  menuLayer="overlay"
                  onChange={(value) =>
                    setDraftFilters((current) => ({
                      ...current,
                      active: value === "all" ? null : value === "active",
                    }))
                  }
                />
              </div>
            </OperationalFilterSurface>
          </div>
        </div>

        <OperationalQueryState
          error={hasRows ? null : userPage.error}
          hasData={hasRows}
          isFetching={userPage.isFetching}
          isFiltered={isFiltered}
          isLoading={userPage.isLoading}
          onRetry={() => void userPage.refetch()}
          renderSkeleton={UserDirectorySkeleton}
          renderEmpty={({ filtered }) => (
            <div className="users-directory__empty">
              <p>
                {filtered
                  ? "Nenhum usuário encontrado para estes filtros."
                  : "Nenhum usuário cadastrado."}
              </p>
            </div>
          )}
        >
          <UserDirectory
            users={users}
            currentUserId={currentUser?.id}
            isFetching={userPage.isFetching}
            canEdit={(user) =>
              !!currentUser &&
              (canActorEditUserIdentity(currentUser, user) ||
                (isClientManager && canActorEditStoreAccess(currentUser, user)))
            }
            onEdit={openEdit}
          />
          <OperationalPager
            ariaLabel="Paginação de usuários"
            isFetching={userPage.isFetching}
            itemLabel="usuário"
            itemLabelPlural="usuários"
            limit={USER_PAGE_SIZE}
            offset={query.offset}
            onPageChange={(page) =>
              setQuery((current) => ({
                ...current,
                offset: page * USER_PAGE_SIZE,
              }))
            }
            totalItems={total}
            variant="numbered"
          />
        </OperationalQueryState>

        {showRefetchError ? (
          <FloatingToast
            title="Não foi possível atualizar."
            message={getUserFacingErrorMessage(
              userPage.error,
              "Tente novamente em alguns instantes.",
            )}
            actionLabel="Tentar novamente"
            onAction={() => {
              setDismissedListError(userPage.error);
              void userPage.refetch();
            }}
            onDismiss={() => setDismissedListError(userPage.error)}
          />
        ) : null}

        <AdminDrawer
          open={drawer !== null}
          onClose={closeDrawer}
          onSubmit={
            drawer?.mode === "create"
              ? handleCreate
              : isStoreAccessOnlyDrawer
                ? handleStoreAccessSubmit
                : handleUpdate
          }
          title={
            drawer?.mode === "create"
              ? "Novo Usuário"
              : isStoreAccessOnlyDrawer
                ? "Acesso às Lojas"
                : "Editar Usuário"
          }
        >
          <div style={adminDrawerBodyStyle}>
            {/* Name */}
            {!isStoreAccessOnlyDrawer && (
              <div style={adminFieldStyle}>
                <label style={adminLabelStyle}>Nome</label>
                <input
                  className="drawer-input"
                  style={adminInputStyle}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome completo"
                />
              </div>
            )}

            {/* Email */}
            {!isStoreAccessOnlyDrawer && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                }}
              >
                <label style={adminLabelStyle}>E-mail</label>
                <input
                  type="email"
                  className="drawer-input"
                  style={adminInputStyle}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                />
              </div>
            )}

            {/* Role */}
            {!isStoreAccessOnlyDrawer && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                }}
              >
                <label style={adminLabelStyle}>Perfil</label>
                <select
                  style={adminSelectStyle}
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value as UserRole);
                    if (
                      e.target.value !== "client" &&
                      e.target.value !== "client_store"
                    ) {
                      setClientId("");
                    }
                    if (e.target.value !== "supplier") setSupplierId("");
                  }}
                >
                  {EDITABLE_USER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Client — only for client/client_store */}
            {!isStoreAccessOnlyDrawer &&
              (role === "client" || role === "client_store") && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.375rem",
                  }}
                >
                  <label style={adminLabelStyle}>Cliente</label>
                  <select
                    style={adminSelectStyle}
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  >
                    <option value="">Selecione um cliente</option>
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {!isStoreAccessOnlyDrawer && role === "supplier" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                }}
              >
                <label style={adminLabelStyle}>Fornecedor</label>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    alignItems: "stretch",
                  }}
                >
                  <select
                    style={{ ...adminSelectStyle, flex: 1, minWidth: "220px" }}
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                  >
                    <option value="">Selecione um fornecedor</option>
                    {supplierOptions.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{
                      ...btnSecondary,
                      flex: "unset",
                      padding: "0.55rem 0.85rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.45rem",
                    }}
                    onClick={openSupplierCreate}
                    disabled={isPending}
                  >
                    <Plus size={14} strokeWidth={1.5} />
                    Novo fornecedor
                  </button>
                </div>
              </div>
            )}

            {/* Password — only on create */}
            {!isStoreAccessOnlyDrawer && drawer?.mode === "create" && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.375rem",
                  }}
                >
                  <label style={adminLabelStyle}>Senha inicial</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="drawer-input"
                      style={{ ...adminInputStyle, paddingRight: "2.5rem" }}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setCopiedPassword(false);
                      }}
                      placeholder="Senha inicial"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="password-toggle"
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      style={toggleButtonStyle}
                    >
                      <span
                        className={`eye-icon ${showPassword ? "eye-open" : "eye-closed"}`}
                      >
                        {showPassword ? (
                          <EyeOff size={16} strokeWidth={1.75} />
                        ) : (
                          <Eye size={16} strokeWidth={1.75} />
                        )}
                      </span>
                    </button>
                  </div>
                  <div
                    style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                  >
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        ...btnSecondary,
                        flex: "unset",
                        padding: "0.55rem 0.85rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.45rem",
                      }}
                      onClick={() => {
                        setPassword(generateInitialPassword());
                        setCopiedPassword(false);
                      }}
                      disabled={isPending}
                    >
                      <RefreshCw size={14} strokeWidth={1.5} />
                      Gerar senha
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        ...btnSecondary,
                        flex: "unset",
                        padding: "0.55rem 0.85rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.45rem",
                        color: copiedPassword
                          ? "var(--login-accent)"
                          : "var(--login-muted)",
                      }}
                      onClick={handleCopyPassword}
                      disabled={isPending || !password}
                    >
                      <Copy size={14} strokeWidth={1.5} />
                      {copiedPassword ? "Copiado" : "Copiar senha"}
                    </button>
                  </div>
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--login-muted)",
                      margin: 0,
                    }}
                  >
                    O usuário será obrigado a trocar no primeiro acesso.
                  </p>
                </div>
              </>
            )}

            {/* Edit-mode actions */}
            {isStoreAccessOnlyDrawer && activeUser && (
              <>
                <div style={adminFieldStyle}>
                  <label style={adminLabelStyle}>Usuário</label>
                  <p
                    style={{
                      margin: 0,
                      color: "var(--login-text)",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {activeUser.name}
                  </p>
                  <p
                    style={{
                      margin: "0.15rem 0 0",
                      color: "var(--login-muted)",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "0.8125rem",
                    }}
                  >
                    {activeUser.email}
                  </p>
                </div>

                <div style={adminFieldStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <label style={adminLabelStyle}>Acesso às lojas</label>
                    {storeScope === "selected" ? (
                      <span
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          color: "var(--login-muted)",
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedStoreCount} de {stores.length} lojas
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "0.25rem",
                      padding: "0.25rem",
                      background: "var(--login-bg)",
                      border: "1px solid var(--login-border)",
                      borderRadius: "0.75rem",
                    }}
                  >
                    <button
                      type="button"
                      className="users-store-scope"
                      aria-label="Todas as lojas atuais e futuras"
                      aria-pressed={storeScope === "all"}
                      onClick={() => setStoreScope("all")}
                      style={{
                        minHeight: "44px",
                        padding: "0.55rem 0.65rem",
                        border: "none",
                        borderRadius: "0.55rem",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        letterSpacing: 0,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.05,
                      }}
                    >
                      <span>Todas</span>
                      <span
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: "0.65rem",
                          fontWeight: 500,
                          letterSpacing: 0,
                          opacity: 0.82,
                          textTransform: "none",
                        }}
                      >
                        atuais e futuras
                      </span>
                    </button>
                    <button
                      type="button"
                      className="users-store-scope"
                      aria-label="Selecionar lojas"
                      aria-pressed={storeScope === "selected"}
                      onClick={() => setStoreScope("selected")}
                      style={{
                        minHeight: "44px",
                        padding: "0.55rem 0.65rem",
                        border: "none",
                        borderRadius: "0.55rem",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        letterSpacing: 0,
                        cursor: "pointer",
                      }}
                    >
                      Selecionar lojas
                    </button>
                  </div>
                </div>

                <div
                  data-testid="store-access-panel"
                  aria-hidden={storeScope !== "selected"}
                  style={{
                    display: "grid",
                    gridTemplateRows: storeScope === "selected" ? "1fr" : "0fr",
                    opacity: storeScope === "selected" ? 1 : 0,
                    transform:
                      storeScope === "selected"
                        ? "translateY(0)"
                        : "translateY(-4px)",
                    marginTop: storeScope === "selected" ? 0 : "-0.25rem",
                    overflow: "hidden",
                    pointerEvents: storeScope === "selected" ? "auto" : "none",
                    transition:
                      "grid-template-rows 0.22s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.16s ease, transform 0.18s ease, margin-top 0.18s ease",
                  }}
                >
                  <div
                    style={{
                      minHeight: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.625rem",
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      <Search
                        size={16}
                        strokeWidth={1.75}
                        style={{
                          position: "absolute",
                          left: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--login-muted)",
                          pointerEvents: "none",
                        }}
                      />
                      <input
                        value={storeSearch}
                        onChange={(event) => setStoreSearch(event.target.value)}
                        placeholder="Buscar loja..."
                        style={{
                          ...adminInputStyle,
                          paddingLeft: "2.25rem",
                          borderRadius: "0.75rem",
                        }}
                      />
                    </div>

                    {stores.length === 0 && (
                      <p style={{ ...adminMutedTextStyle, margin: 0 }}>
                        Nenhuma loja disponível.
                      </p>
                    )}
                    {stores.length > 0 && filteredAccessStores.length === 0 && (
                      <p style={{ ...adminMutedTextStyle, margin: 0 }}>
                        Nenhuma loja encontrada.
                      </p>
                    )}
                    {filteredAccessStores.length > 0 && (
                      <div
                        data-testid="store-access-list"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          maxHeight: "min(45vh, 420px)",
                          overflowY: "auto",
                          paddingRight: "0.15rem",
                          overscrollBehavior: "contain",
                        }}
                      >
                        {filteredAccessStores.map((store) => {
                          const checked = selectedStoreIds.includes(store.id);
                          return (
                            <label
                              key={store.id}
                              className="users-store-check"
                              style={{
                                display: "flex",
                                gap: "0.625rem",
                                alignItems: "center",
                                color: "var(--login-text)",
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "0.875rem",
                                padding: "0.72rem",
                                border: `1px solid ${checked ? "var(--login-accent)" : "var(--login-border)"}`,
                                borderRadius: "0.75rem",
                                background: checked
                                  ? "color-mix(in srgb, var(--login-accent) 8%, var(--login-surface))"
                                  : "var(--login-surface)",
                                cursor: "pointer",
                                transition:
                                  "border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease",
                              }}
                            >
                              <input
                                aria-label={store.name}
                                type="checkbox"
                                checked={checked}
                                onChange={(event) =>
                                  toggleStoreSelection(
                                    store.id,
                                    event.target.checked,
                                  )
                                }
                                style={{ position: "absolute", opacity: 0 }}
                              />
                              <span
                                aria-hidden="true"
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  borderRadius: "0.375rem",
                                  border: `1px solid ${checked ? "var(--login-accent)" : "var(--login-border)"}`,
                                  background: checked
                                    ? "var(--login-accent)"
                                    : "transparent",
                                  color: "var(--users-on-primary)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  transition:
                                    "border-color 0.15s ease, background 0.15s ease",
                                }}
                              >
                                {checked ? (
                                  <Check size={14} strokeWidth={2} />
                                ) : null}
                              </span>
                              <span style={{ minWidth: 0, flex: 1 }}>
                                <span
                                  style={{
                                    display: "block",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {store.name}
                                </span>
                                <span
                                  style={{
                                    display: "block",
                                    color: "var(--login-muted)",
                                    fontSize: "0.75rem",
                                    marginTop: "0.1rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {store.address}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!isStoreAccessOnlyDrawer &&
              drawer?.mode === "edit" &&
              activeUser && (
                <div
                  style={{
                    borderTop: "1px solid var(--login-border)",
                    paddingTop: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.625rem",
                  }}
                >
                  <p
                    style={{
                      ...adminLabelStyle,
                      fontSize: "0.75rem",
                      margin: 0,
                    }}
                  >
                    Ações
                  </p>

                  {/* Deactivate / Reactivate */}
                  {!isSelf &&
                    (isDeactivatedUser ? (
                      <button
                        className="btn-secondary"
                        style={{
                          ...btnSecondary,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          justifyContent: "center",
                          color: "var(--login-accent)",
                        }}
                        disabled={isPending}
                        onClick={handleReactivate}
                      >
                        <RefreshCw size={14} strokeWidth={1.5} />
                        Reativar conta
                      </button>
                    ) : (
                      <button
                        className="btn-secondary"
                        style={{
                          ...btnSecondary,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          justifyContent: "center",
                          color: "#f59e0b",
                        }}
                        disabled={isPending}
                        onClick={handleDeactivate}
                      >
                        <AlertTriangle size={14} strokeWidth={1.5} />
                        Desativar conta
                      </button>
                    ))}

                  {/* Reset Password */}
                  {!confirmReset ? (
                    <button
                      className="btn-secondary"
                      style={{
                        ...btnSecondary,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        justifyContent: "center",
                      }}
                      disabled={isPending}
                      onClick={() => setConfirmReset(true)}
                    >
                      <KeyRound size={14} strokeWidth={1.5} />
                      Redefinir senha
                    </button>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <input
                          type={showResetPassword ? "text" : "password"}
                          className="drawer-input"
                          style={{ ...adminInputStyle, paddingRight: "2.5rem" }}
                          value={resetPassword}
                          onChange={(e) =>
                            setResetPasswordValue(e.target.value)
                          }
                          placeholder="Nova senha temporária"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPassword((v) => !v)}
                          className="password-toggle"
                          aria-label={
                            showResetPassword
                              ? "Ocultar senha"
                              : "Mostrar senha"
                          }
                          style={toggleButtonStyle}
                        >
                          <span
                            className={`eye-icon ${showResetPassword ? "eye-open" : "eye-closed"}`}
                          >
                            {showResetPassword ? (
                              <EyeOff size={16} strokeWidth={1.75} />
                            ) : (
                              <Eye size={16} strokeWidth={1.75} />
                            )}
                          </span>
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          style={{
                            ...btnSecondary,
                            flex: "none",
                            padding: "0.5rem 1rem",
                          }}
                          onClick={() => {
                            setConfirmReset(false);
                            setResetPasswordValue("");
                          }}
                          disabled={isPending}
                        >
                          Cancelar
                        </button>
                        <button
                          className="btn-primary"
                          style={{
                            ...btnPrimary,
                            fontSize: "0.875rem",
                            padding: "0.5rem 1rem",
                          }}
                          onClick={handleResetPassword}
                          disabled={isPending || !resetPassword.trim()}
                        >
                          Confirmar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Delete */}
                  {!isSelf &&
                    (!confirmDelete ? (
                      <button
                        className="btn-danger"
                        style={{
                          ...btnDanger,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          justifyContent: "center",
                        }}
                        disabled={isPending}
                        onClick={() => setConfirmDelete(true)}
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                        Excluir usuário
                      </button>
                    ) : (
                      <button
                        className="btn-danger"
                        style={{
                          ...btnDanger,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          justifyContent: "center",
                        }}
                        disabled={isPending}
                        onClick={handleDelete}
                      >
                        Confirmar exclusão permanente
                      </button>
                    ))}
                </div>
              )}

            {/* Error */}
            {mutationError && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--login-error, #c44e3a)",
                  margin: 0,
                }}
              >
                {getUserFacingErrorMessage(
                  mutationError,
                  "Não foi possível salvar o usuário. Tente novamente.",
                )}
              </p>
            )}
          </div>

          {/* Primary action */}
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
            <button
              className="btn-primary"
              style={btnPrimary}
              disabled={isPending}
              onClick={
                drawer?.mode === "create"
                  ? handleCreate
                  : isStoreAccessOnlyDrawer
                    ? handleStoreAccessSubmit
                    : handleUpdate
              }
            >
              {drawer?.mode === "create"
                ? "Criar usuário"
                : isStoreAccessOnlyDrawer
                  ? "Salvar acesso"
                  : "Salvar"}
            </button>
          </div>
        </AdminDrawer>

        <AdminDrawer
          open={supplierCreateOpen}
          onClose={closeSupplierCreate}
          onSubmit={handleCreateSupplierInline}
          title="Novo Fornecedor"
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label style={adminLabelStyle}>Nome</label>
              <input
                className="drawer-input"
                style={adminInputStyle}
                value={newSupplierName}
                onChange={(e) => setNewSupplierName(e.target.value)}
                placeholder="Nome do fornecedor"
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label style={adminLabelStyle}>E-mail</label>
              <input
                className="drawer-input"
                style={adminInputStyle}
                value={newSupplierEmail}
                onChange={(e) => setNewSupplierEmail(e.target.value)}
                placeholder="email@fornecedor.com"
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label style={adminLabelStyle}>Telefone</label>
              <input
                className="drawer-input"
                style={adminInputStyle}
                value={newSupplierPhone}
                onChange={(e) => setNewSupplierPhone(e.target.value)}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                className="btn-primary"
                style={btnPrimary}
                onClick={handleCreateSupplierInline}
                disabled={isPending || !newSupplierName.trim()}
              >
                Criar fornecedor
              </button>
              <button
                className="btn-secondary"
                style={btnSecondary}
                onClick={closeSupplierCreate}
                disabled={isPending}
              >
                Cancelar
              </button>
            </div>
            {createSupplierMutation.error ? (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--login-error, #c44e3a)",
                  margin: 0,
                }}
              >
                {getUserFacingErrorMessage(
                  createSupplierMutation.error,
                  "Não foi possível criar o fornecedor. Tente novamente.",
                )}
              </p>
            ) : null}
          </div>
        </AdminDrawer>
      </AdminPageShell>
    </>
  );
}
